import { defineStore } from 'pinia';
import { computed, ref } from 'vue-demi';
import jwtDecode from 'jwt-decode';
import type { AccessTokenPayload } from '@ilteducation/auth';
import { tokensByRefreshToken } from './api';
import { load, persist, remove } from './storage';
import { checkIsOnline } from './network';
import type { Tokens } from './domain';

const STORAGE_KEY_TOKENS = 'ilt-auth.tokens';

type State = {
  accessToken: string;
  clientId?: string;
  idToken?: string;
  refreshToken?: string;
};

type TokensBroadcastMessage = Partial<Tokens>;

export type ClearTokensOptions = {
  /**
   * Losing the access token is broadcast so the other tabs can react to a logout, and their handler
   * answers it with a page reload. That is wrong for the clear that precedes exchanging a code:
   * it is not a logout but the first half of installing new tokens, and a reloaded tab starts a
   * login of its own, which is how one login became several competing ones.
   */
  notifyOtherTabs?: boolean;
};

/**
 * Check if the token is valid long enough to be used for one more request.
 *
 */
export const isFreshEnough = (tokens: Tokens): boolean => {
  const { exp: tokenExpiryTimestampInSeconds } = jwtDecode(tokens.accessToken) as AccessTokenPayload;
  const nowTimestampInSeconds = Date.now() / 1000;

  /*
  This assumes that the token is initially valid for at least this amount of  seconds.
  Otherwise, we get into a token refreshing loop, because a new token is already considered stale.
   */
  const maxTimeNeededForARequestInSeconds = 60 * 5;

  return tokenExpiryTimestampInSeconds - nowTimestampInSeconds >= maxTimeNeededForARequestInSeconds;
};

/**
 * Check if the refresh token has expired (or is about to), using the access token's `iat`
 * since both are co-issued.
 */
const isRefreshTokenExpired = (tokens: Tokens): boolean => {
  const { iat } = jwtDecode(tokens.accessToken) as AccessTokenPayload;
  const REFRESH_TOKEN_TTL_IN_SECONDS = 3600 * 24 * 180; // 180 days (≈6 months), matches authena's RefreshToken.TTL_IN_MS
  const buffer = 60 * 5; // 5-minute buffer to account for the time it could take for a request to reach the server

  // Subtract the buffer to treat the refresh token as expired slightly early.
  return iat + REFRESH_TOKEN_TTL_IN_SECONDS - buffer < Date.now() / 1000;
};

/*
We want to allow only one request to get the valid tokens at a time.

This is because we don't want to refresh the token multiple times when multiple requests are made at the same time.
 */
let getValidTokensPromise: Promise<Tokens | null> | null = null;

const useTokens = defineStore('ilt-auth-tokens', () => {
  const state = ref<State>();
  const isInitialised = ref(false);

  const acceptTokens = async (tokens: Tokens) => {
    await persist(STORAGE_KEY_TOKENS, tokens);

    let clientId;

    const { refreshToken } = tokens;
    if (refreshToken) {
      const { aud } = jwtDecode(tokens.accessToken) as AccessTokenPayload;

      clientId = aud;
    }

    state.value = { ...tokens, clientId };
  };

  const tokensUpdateChannel = 'BroadcastChannel' in window ? new BroadcastChannel('ilt-auth-tokens-update') : null;

  const postTokensUpdate = (data: TokensBroadcastMessage) => {
    if (!tokensUpdateChannel) return;
    tokensUpdateChannel.postMessage(data);
  };

  const storeAndBroadcastTokens = async (tokens: Tokens) => {
    postTokensUpdate(tokens);
    await acceptTokens(tokens);
  };

  if (tokensUpdateChannel) {
    tokensUpdateChannel.onmessage = async (event: MessageEvent<TokensBroadcastMessage>) => {
      const receivedAccessToken = event.data.accessToken;
      const accessTokenStoredInThisContext = state.value?.accessToken;

      // If we were not logged in, and now we are, or the other way around, we refresh the page also
      if (
        (accessTokenStoredInThisContext && !receivedAccessToken) ||
        (!accessTokenStoredInThisContext && receivedAccessToken)
      ) {
        window.location.reload();
        return;
      }

      if (receivedAccessToken) {
        // If the tokens just updated, we store them in this tab also
        await acceptTokens({
          accessToken: receivedAccessToken,
          refreshToken: event.data.refreshToken,
          idToken: event.data.idToken,
        });
      }
    };
  }

  const clearTokens = async ({ notifyOtherTabs = true }: ClearTokensOptions = {}) => {
    await remove(STORAGE_KEY_TOKENS);

    state.value = undefined;

    if (!notifyOtherTabs) {
      return;
    }

    postTokensUpdate({
      accessToken: undefined,
      refreshToken: undefined,
      idToken: undefined,
    });
  };

  const refreshTokens = async (tokens: Tokens, clientId: string): Promise<void> => {
    const isOnline = await checkIsOnline();
    if (!isOnline) {
      // simply pass the old tokens
      console.log("App is offline, so we're using the old STUFF THAT GIVES US ACCESS"); // Avoid using "token" word in logs since they are filtered by sentry
      return acceptTokens(tokens);
    }

    return tokensByRefreshToken({
      refreshToken: tokens.refreshToken!,
      clientId,
    })
      .then((freshTokens) => {
        return storeAndBroadcastTokens(freshTokens);
      })
      .catch(() => clearTokens());
  };

  const forceTokensRefresh = async () =>
    load<Tokens>(STORAGE_KEY_TOKENS).then((persistedTokens) => {
      if (!persistedTokens) {
        throw new Error('No persisted STUFF available'); // Avoid using "token" word in logs since they are filtered by sentry
      }

      const { aud } = jwtDecode(persistedTokens.accessToken) as AccessTokenPayload;
      return refreshTokens(persistedTokens, aud);
    });

  const tokens = computed(() =>
    state.value ? { idToken: state.value.idToken, accessToken: state.value.accessToken } : null,
  );

  // Load persisted tokens on initial load
  load<Tokens>(STORAGE_KEY_TOKENS).then((persistedTokens) => {
    if (!persistedTokens) {
      isInitialised.value = true;
      return;
    }

    acceptTokens(persistedTokens).finally(() => {
      isInitialised.value = true;
    });
  });

  // TODO - document that in case we are offline, we return the old tokens
  const refreshOldToken = async (oldTokens: Tokens): Promise<Tokens> => {
    const isOnline = await checkIsOnline();
    if (!isOnline) {
      console.log('App is offline, so we can not refresh the old stuff, unfortunately'); // Avoid using "token" word in logs since they are filtered by sentry

      return oldTokens;
    }
    if (!oldTokens.refreshToken) {
      throw new Error('No refresh RESOURCE available'); // Avoid using "token" word in logs since they are filtered by sentry
    }

    if (isRefreshTokenExpired(oldTokens)) {
      throw new Error('Refresh token has expired');
    }

    const { aud } = jwtDecode(oldTokens.accessToken) as AccessTokenPayload;
    const freshlySqueezedTokens = await tokensByRefreshToken({
      refreshToken: oldTokens.refreshToken,
      clientId: aud,
    });

    await storeAndBroadcastTokens(freshlySqueezedTokens);

    return freshlySqueezedTokens;
  };

  /**
   * Returns the current tokens if they are valid, otherwise tries to refresh them and get the fresh one 🍋
   */
  const getValidTokens = async (): Promise<Tokens | null> => {
    if (getValidTokensPromise) {
      return getValidTokensPromise;
    }

    const getTokens = async (): Promise<Tokens | null> => {
      const persistedTokens = await load<Tokens>(STORAGE_KEY_TOKENS);

      if (!persistedTokens) return null;

      // Stored token is still fine
      if (isFreshEnough(persistedTokens)) return persistedTokens;

      // Token is expired or about to expire, let's refresh it
      return refreshOldToken(persistedTokens);
    };

    /*
    Usage of web locks is guarded by `navigator?.locks` because the education app targets an older TypeScript lib
    that doesn't include the Web Locks API, causing a build-time TS2339 error.
    */
    if ('locks' in navigator) {
      /*
      Cross-tab lock (web locks): prevents multiple tabs from sending the same refresh token request simultaneously,
      mitigating issues where one request fails and logs the user out.
      */
      getValidTokensPromise = (
        (navigator as any).locks.request('ilt-auth-token-refresh', getTokens) as Promise<Tokens | null>
      )
        .catch((err) => {
          // We log out if we can't get the tokens
          clearTokens();
          throw err;
        })
        .finally(() => {
          getValidTokensPromise = null;
        });
    } else {
      getValidTokensPromise = new Promise<Tokens | null>((resolve, reject) => {
        getTokens()
          .then((freshTokens) => {
            resolve(freshTokens);
          })
          .catch((err) => {
            // We log out if we can't get the tokens
            clearTokens();
            reject(err);
          });
      }).finally(() => {
        getValidTokensPromise = null;
      });
    }

    return getValidTokensPromise;
  };

  return {
    isInitialised,
    tokens,
    getValidTokens,
    acceptTokens,
    storeAndBroadcastTokens,
    clearTokens,
    forceTokensRefresh,
  };
});

export default useTokens;
